import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Header from "./Header";
import "../styles/auth.css";
import "../styles/verify.css";
import { TextField, CircularProgress, Button } from '@mui/material';

/**
 * Might need to implement verification code in the future.
 * Leaving this in the codebase for now even though it is not being used anywhere.
 */
const VerifyCode = () => {
    const location = useLocation();
    const email = location.state?.email || '';
    const source = location.state?.source || '';
    const phoneFromRegister = location.state?.phoneNumber || '';

    const [phone, setPhone] = useState(source === 'register' ? phoneFromRegister : '');
    const [code, setCode] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [showPhoneInput, setShowPhoneInput] = useState(false);
    const [sendingSms, setSendingSms] = useState(false);
    const [isSmsMode, setIsSmsMode] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (!email || !source) {
            if (source === '/register') {
                navigate('/register', { replace: true });
            } else if (source === 'forgot-password') {
                navigate('/forgot-password', { replace: true });
            } else {
                navigate('/login', { replace: true });
            }
        }
    }, [email, source, navigate]);

    const handleVerifyCode = async () => {
        setLoading(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-code`, { email, code, source });
            setMessage(res.data.message);

            if (res.data.token) {
                localStorage.setItem('token', res.data.token);
            }

            // redirect user to their profile page ("/profile") if they just registered.
            // otherwise, redirect them to the "reset password" page ("/reset-password") if they clicked forgot password.
            if (source === 'register' || source === 'login') {
                navigate('/profile');
            } else if (source === 'forgot-password') {
                navigate('/reset-password', { state: { email } });
            }
        } catch (error) {
            setMessage(error.response?.data?.message || 'Error verifying code');
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        setResending(true);
        setIsSmsMode(false);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/resend-code`, { email });
            if (res.status === 200) {
                setMessage('Verification code resent. Check your inbox.');
            } else {
                setMessage(res.data.error || 'Failed to resend code.');
            }
        } catch (error) {
            setMessage('Failed to resend code.');
        } finally {
            setResending(false);
        }
    };

    const handleSendSmsCode = async () => {
        setSendingSms(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/send-code-sms`, {
                phone,
                email
            });

            if (res.status === 200) {
                setMessage('Verification code sent via SMS.');
                setIsSmsMode(true);
            } else {
                setMessage('Failed to send SMS.');
            }
        } catch (error) {
            setMessage('Failed to send SMS.');
        } finally {
            setSendingSms(false);
        }
    };

    return (
        <div>
            <Header />
            <div className="auth-container">
                <div className="auth-box verify-box">
                    <h2>Check your inbox</h2>
                    <p>
                        {isSmsMode 
                            ? <>We have sent a verification code to your phone number: <strong>{phone}</strong></>
                            : <>We have sent a verification code to your email: <strong>{email}</strong></>
                        }
                    </p>
                    <TextField
                        type="text"
                        placeholder="Enter verification code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        fullWidth
                        margin="normal"
                        className="verify-input"
                        required
                    />
                    <div className="verify-buttons">
                        <Button
                            onClick={handleVerifyCode}
                            loading={loading}
                            variant="contained"
                            className="verify-code"
                            loadingIndicator={
                                <CircularProgress size={20} thickness={4} style={{ color: '#ffffff' }}/>
                            }
                        >
                            <span className={loading ? 'button-label-hidden' : ''}>
                                Verify
                            </span>
                        </Button>
                    </div>
                    <p className="auth-links">
                        Didn't receive a code? 
                        <button 
                            onClick={handleResendCode} 
                            className="resend-button"
                        >
                            {resending ? <CircularProgress size={16} color="inherit" /> : "Resend Email" }
                        </button>
                    </p>

                    <span 
                        className="sms-button" 
                        onClick={() => {
                                    setShowPhoneInput(!showPhoneInput);
                                    setMessage('');
                                }}
                    >
                        Send Code via SMS
                    </span>
                    {showPhoneInput && (
                        <div className="sms-phone-input">
                            <TextField
                                type="tel"
                                placeholder="Enter your phone number"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                fullWidth
                                margin="normal"
                                required 
                            />
                            <Button 
                                onClick={handleSendSmsCode}
                                variant="outlined"
                                className="sms-send-button"
                                disabled={sendingSms || !phone}
                            >
                                {sendingSms ? (
                                    <CircularProgress size={16} thickness={4} />
                                ) : (
                                    'Send SMS Code'
                                )}
                            </Button>
                        </div>
                    )}
                    {message && <p className="auth-error">{message}</p>}
                </div>
            </div>
        </div>

    );
};

export default VerifyCode;
